import { useEffect } from "react";
import { Footer } from "@/components/landing/Footer";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ArrowRight, Clock, Download, Printer, ChevronRight } from "lucide-react";
import { getPaper, relatedPapers, TRACK_LABELS } from "@/data/research";
import { useSeo, JsonLd, paperJsonLd } from "@/lib/seo";

const S = { fill: "none", stroke: "#BACD1C", strokeWidth: 1.6, strokeLinejoin: "round", strokeLinecap: "round" };
const G = { fill: "none", stroke: "rgba(255,255,255,0.25)", strokeWidth: 1, strokeDasharray: "3 3" };
const L = { fontFamily: "monospace", fontSize: 9, fill: "rgba(255,255,255,0.4)" };

const FigureArt = ({ kind }) => {
  switch (kind) {
    case "funnel":
      return (
        <svg viewBox="0 0 400 130" className="h-full w-full">
          {[60, 110, 160, 210, 260, 300].map((x) => <circle key={x} cx={x} cy={16} r="3" fill="#BACD1C" opacity="0.7" />)}
          <polygon points="50,30 350,30 250,102 150,102" {...S} opacity="0.6" />
          <line x1="70" y1="55" x2="330" y2="55" {...G} /><text x="60" y="52" {...L}>CONF ≥ 5/6</text>
          <line x1="110" y1="80" x2="290" y2="80" {...G} /><text x="60" y="77" {...L}>QUAL ≥ 65</text>
          <circle cx="185" cy="102" r="3.5" fill="#BACD1C" /><circle cx="215" cy="102" r="3.5" fill="#BACD1C" />
          <text x="170" y="122" {...L} fill="#BACD1C">SETUP PRINTS</text>
        </svg>
      );
    case "expectancy":
      return (
        <svg viewBox="0 0 400 130" className="h-full w-full">
          <line x1="45" y1="15" x2="45" y2="110" {...G} /><line x1="45" y1="110" x2="380" y2="110" {...G} />
          <path d="M60,105 C150,100 200,55 380,25" {...S} />
          <path d="M60,105 C150,100 200,55 380,25 L380,15 L60,15 Z" fill="#BACD1C" opacity="0.07" />
          <text x="150" y="45" {...L} fill="#BACD1C">PROFITABLE</text>
          <text x="150" y="126" {...L}>completion rate →</text>
          <text x="10" y="60" {...L} transform="rotate(-90 14 60)">avg reward →</text>
        </svg>
      );
    case "mtf":
      return (
        <svg viewBox="0 0 400 130" className="h-full w-full">
          {[["1H", 28], ["4H", 62], ["D", 96]].map(([t, y]) => (
            <g key={t}>
              <text x="30" y={y + 4} {...L}>{t}</text>
              <rect x="70" y={y - 8} width="250" height="16" fill="#BACD1C" opacity="0.12" />
              <rect x="70" y={y - 8} width="250" height="16" {...S} opacity="0.5" />
              <path d={`M338,${y + 5} L344,${y - 6} L350,${y + 5}`} {...S} />
              <text x="150" y={y + 4} {...L} fill="#BACD1C">▲ BULL</text>
            </g>
          ))}
          <text x="70" y="122" {...L} fill="#BACD1C">ALIGNED · B/B/B</text>
        </svg>
      );
    case "efficiency":
      return (
        <svg viewBox="0 0 400 130" className="h-full w-full">
          <line x1="200" y1="12" x2="200" y2="118" {...G} />
          <text x="55" y="24" {...L} fill="#BACD1C">HIGH ER — tight</text>
          <path d="M30,100 C70,95 110,45 175,25" {...S} />
          <path d="M30,104 C70,99 110,50 175,30" stroke="#BACD1C" strokeWidth="1" fill="none" opacity="0.4" />
          <text x="245" y="24" {...L}>LOW ER — flat</text>
          <path d="M215,70 L235,55 L250,80 L265,52 L285,78 L305,58 L325,74 L345,60 L370,72" {...S} opacity="0.7" />
          <line x1="215" y1="66" x2="370" y2="66" stroke="#BACD1C" strokeWidth="1.4" opacity="0.5" />
        </svg>
      );
    case "repaint":
      return (
        <svg viewBox="0 0 400 130" className="h-full w-full">
          <g opacity="0.6">
            <line x1="110" y1="25" x2="110" y2="100" {...G} />
            <rect x="95" y="45" width="30" height="40" fill="none" stroke="rgba(255,255,255,0.35)" strokeDasharray="3 3" />
            <text x="70" y="118" {...L}>intrabar ? (ignored)</text>
          </g>
          <g>
            <line x1="285" y1="20" x2="285" y2="105" {...S} />
            <rect x="270" y="40" width="30" height="45" fill="#BACD1C" opacity="0.18" />
            <rect x="270" y="40" width="30" height="45" {...S} />
            <path d="M312,42 L320,52 L336,30" {...S} />
            <text x="240" y="118" {...L} fill="#BACD1C">confirmed close ✓</text>
          </g>
        </svg>
      );
    case "gauge":
      return (
        <svg viewBox="0 0 400 130" className="h-full w-full">
          <path d="M70,110 A130,130 0 0,1 330,110" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="10" />
          <path d="M70,110 A130,130 0 0,1 200,45" fill="none" stroke="#ff5c5c" strokeWidth="10" opacity="0.6" />
          <path d="M200,45 A130,130 0 0,1 330,110" fill="none" stroke="#BACD1C" strokeWidth="10" opacity="0.7" />
          <line x1="200" y1="110" x2="255" y2="62" {...S} strokeWidth="2.4" />
          <circle cx="200" cy="110" r="5" fill="#BACD1C" />
          <text x="55" y="126" {...L}>FEAR</text><text x="305" y="126" {...L} fill="#BACD1C">GREED</text>
        </svg>
      );
    case "bullflag":
      return (
        <svg viewBox="0 0 400 130" className="h-full w-full">
          <path d="M40,110 L120,30" {...S} strokeWidth="2" />
          <text x="46" y="70" {...L}>pole</text>
          <line x1="120" y1="30" x2="200" y2="52" {...G} /><line x1="120" y1="55" x2="200" y2="77" {...G} />
          <path d="M120,30 L145,50 L160,38 L180,60 L200,50" {...S} opacity="0.8" />
          <path d="M200,50 L235,20 L245,10" {...S} strokeWidth="2" />
          <path d="M245,10 L245,22 M245,10 L233,10" {...S} />
          <text x="215" y="45" {...L} fill="#BACD1C">breakout</text>
        </svg>
      );
    case "cup":
      return (
        <svg viewBox="0 0 400 130" className="h-full w-full">
          <line x1="60" y1="35" x2="360" y2="35" {...G} /><text x="300" y="30" {...L}>rim</text>
          <path d="M60,35 C90,120 230,120 260,40" {...S} />
          <path d="M260,40 C272,58 292,58 300,42" {...S} />
          <path d="M300,42 L340,18 L350,8 M350,8 L350,20 M350,8 L338,8" {...S} strokeWidth="2" />
          <text x="120" y="100" {...L}>rounded base</text>
        </svg>
      );
    case "hns":
      return (
        <svg viewBox="0 0 400 130" className="h-full w-full">
          <path d="M30,90 L80,50 L120,78 L200,25 L280,78 L320,55 L370,100" {...S} />
          <line x1="95" y1="78" x2="300" y2="78" {...G} /><text x="150" y="72" {...L}>neckline</text>
          <text x="60" y="45" {...L}>LS</text><text x="188" y="20" {...L}>HEAD</text><text x="305" y="50" {...L}>RS</text>
          <path d="M320,88 L320,105 M320,105 L312,97 M320,105 L328,97" {...S} strokeWidth="1.4" />
        </svg>
      );
    case "doublebottom":
      return (
        <svg viewBox="0 0 400 130" className="h-full w-full">
          <path d="M40,30 L110,95 L200,50 L290,95 L360,25" {...S} />
          <line x1="90" y1="95" x2="310" y2="95" {...G} /><text x="150" y="112" {...L}>support (2 tests)</text>
          <line x1="60" y1="50" x2="360" y2="50" stroke="#BACD1C" strokeWidth="1.2" opacity="0.5" strokeDasharray="4 3" />
          <text x="250" y="44" {...L} fill="#BACD1C">confirmation</text>
        </svg>
      );
    case "triangle":
      return (
        <svg viewBox="0 0 400 130" className="h-full w-full">
          <line x1="50" y1="35" x2="340" y2="35" stroke="#BACD1C" strokeWidth="1.4" opacity="0.6" />
          <text x="55" y="28" {...L}>resistance</text>
          <line x1="50" y1="110" x2="320" y2="45" {...G} />
          <path d="M60,70 L100,45 L130,72 L170,42 L205,66 L245,40 L285,55 L330,18 L342,8" {...S} opacity="0.85" />
          <path d="M342,8 L342,20 M342,8 L330,8" {...S} />
          <text x="150" y="105" {...L}>higher lows →</text>
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 400 120" className="h-full w-full">
          <path d="M0,90 C60,88 90,40 140,42 C190,44 210,20 260,26 C310,32 340,60 400,30" {...S} />
        </svg>
      );
  }
};

// Illustrative figure block (clearly labelled — never presented as real data).
const Figure = ({ n, kind, caption }) => (
  <figure className="my-8 border border-white/[0.10] bg-[#13151A] overflow-hidden">
    <div className="relative h-40 bg-[#0A0A0A] tech-grid p-2">
      <div className="pointer-events-none absolute inset-0 lime-glow opacity-15" />
      <div className="relative h-full w-full opacity-90">
        <FigureArt kind={kind} />
      </div>
      <span className="absolute top-2 right-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#7D8390] border border-white/[0.10] px-1.5 py-0.5 bg-black/50">
        Fig {n} · illustrative
      </span>
    </div>
    <figcaption className="border-t border-white/10 px-4 py-3 font-mono text-[11px] text-white/45 leading-relaxed">
      Figure {n} — {caption}
    </figcaption>
  </figure>
);

export default function ResearchPaper() {
  const { slug } = useParams();
  const paper = getPaper(slug);

  useSeo({
    title: paper ? `${paper.title} — TradrQuant Quant Lab` : "Research — TradrQuant",
    description: paper ? paper.abstract.slice(0, 300) : "",
    path: `/research/${slug}`,
    type: "article",
  });
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!paper) return <Navigate to="/research" replace />;

  const trackLabel = TRACK_LABELS[paper.track];
  const related = relatedPapers(paper);
  const figNums = {};
  let figCount = 0;
  paper.sections.forEach((s, i) => { if (s.figure) figNums[i] = ++figCount; });

  return (
    <div data-testid="research-paper" className="grain relative min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <header className="no-print sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[820px] px-5 md:px-8 h-14 flex items-center justify-between">
          <Link to="/research" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-[#BACD1C] transition-colors duration-300">
            <ChevronLeft size={14} /> Quant Lab
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="paper-download-pdf"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-black bg-[#BACD1C] px-3.5 py-2 hover:bg-white transition-colors duration-300"
          >
            <Download size={13} /> PDF
          </button>
        </div>
      </header>

      <article className="paper-doc mx-auto max-w-[820px] px-5 md:px-8 pt-10 md:pt-14 pb-20">
        {/* breadcrumb */}
        <nav className="no-print flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
          <Link to="/" className="hover:text-[#BACD1C]">Home</Link>
          <ChevronRight size={11} />
          <Link to="/research" className="hover:text-[#BACD1C]">Quant Lab</Link>
          <ChevronRight size={11} />
          <span className="text-white/50 truncate">{trackLabel}</span>
        </nav>

        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">// {trackLabel} · Whitepaper</p>
        <h1 className="mt-4 font-display font-semibold tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl leading-[1.02]">
          {paper.title}
        </h1>
        <p className="mt-5 text-sm sm:text-base text-white/55 leading-relaxed">{paper.subtitle}</p>

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/35 border-y border-white/10 py-3">
          <span>TradrQuant Research</span>
          <span className="flex items-center gap-1.5"><Clock size={11} /> {paper.readMins} min read</span>
          <span>Updated {paper.updated}</span>
        </div>

        {/* abstract */}
        <div className="mt-8 border-l-2 border-[#BACD1C] pl-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7D8390] mb-2">Abstract</p>
          <p className="text-[13px] text-white/65 leading-relaxed">{paper.abstract}</p>
        </div>

        {/* sections */}
        <div className="mt-10 space-y-10">
          {paper.sections.map((s, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="font-display font-semibold tracking-[-0.03em] text-xl sm:text-2xl leading-snug">
                <span className="text-[#BACD1C]/50 mr-2 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                {s.h}
              </h2>
              {s.p && s.p.map((para, pi) => (
                <p key={pi} className="mt-4 font-mono text-[13px] sm:text-sm text-white/60 leading-[1.8]">{para}</p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-2">
                  {s.list.map((li, li2) => (
                    <li key={li2} className="flex gap-3 text-[13px] text-white/60 leading-relaxed">
                      <span className="text-[#BACD1C] shrink-0">›</span>
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.figure && <Figure n={figNums[i]} kind={s.figure.kind} caption={s.figure.caption} />}
            </motion.section>
          ))}
        </div>

        {/* key findings */}
        <div className="mt-12 border border-white/[0.10] bg-[#13151A] p-6 sm:p-7">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B4BAC4]">// Key findings</p>
          <ul className="mt-4 space-y-3">
            {paper.keyFindings.map((k, i) => (
              <li key={i} className="flex gap-3 text-[13px] text-white/70 leading-relaxed">
                <span className="text-[#BACD1C] shrink-0 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <span>{k}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* FAQ */}
        {paper.faqs && (
          <div className="mt-12">
            <h2 className="font-display font-semibold tracking-[-0.03em] text-xl sm:text-2xl">Frequently asked</h2>
            <div className="mt-5 space-y-4">
              {paper.faqs.map((f, i) => (
                <div key={i} className="border border-white/10 bg-[#13151A] p-5">
                  <p className="font-display font-semibold tracking-[-0.03em] text-[15px] text-white/90">{f.q}</p>
                  <p className="mt-2 text-[13px] text-white/55 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* references */}
        <div className="mt-12">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">// References & further reading</p>
          <ol className="mt-4 space-y-2 list-decimal list-inside">
            {paper.references.map((r, i) => (
              <li key={i} className="font-mono text-[12px] text-white/45 leading-relaxed">{r.text}</li>
            ))}
          </ol>
        </div>

        {/* CTA */}
        <div className="no-print mt-12 border border-white/[0.10] bg-[#0D0E11] p-6 relative overflow-hidden flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="absolute inset-0 lime-glow opacity-25" />
          <p className="relative font-display font-semibold tracking-[-0.03em] text-lg leading-tight max-w-md">
            See the engine in action. <span className="text-[#7D8390]">Meet the Scanner.</span>
          </p>
          <Link
            to="/#signal-scanner"
            className="relative shrink-0 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] bg-[#BACD1C] text-black px-5 py-3 hover:bg-white active:scale-95 transition-[background-color,transform] duration-300"
          >
            Explore the Scanner <ArrowRight size={14} />
          </Link>
        </div>

        {/* related */}
        {related.length > 0 && (
          <div className="no-print mt-12">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">// Related papers</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/research/${r.slug}`}
                  data-testid={`related-paper-${r.slug}`}
                  className="group border border-white/10 bg-[#13151A] p-5 hover:border-[#BACD1C]/40 transition-colors duration-300"
                >
                  <h3 className="font-display font-semibold tracking-[-0.03em] text-[15px] leading-snug group-hover:text-[#BACD1C] transition-colors">{r.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7D8390]">Read <ArrowRight size={11} /></span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <p className="mt-12 font-mono text-[10px] text-white/25 leading-relaxed">
          // Educational & methodology content only — not financial advice. Figures are conceptual and illustrative
          and do not represent verified trading results. Past performance does not guarantee future results.
        </p>
      </article>
      <Footer />
    </div>
  );
}
